import React, { useEffect, useRef } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, Container, Typography, useTheme } from '@mui/material';

import { useStickyHeaderDetection } from '../hooks/useStickyHeaderDetection';

interface BackButtonConfig {
  label: string;
  onClick: () => void;
}

interface StickyHeaderLayoutProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  backButton?: BackButtonConfig;
  children: React.ReactNode;
  containerId?: string; // Optional unique ID for multiple headers on same page
}

export const StickyHeaderLayout: React.FC<StickyHeaderLayoutProps> = ({
  title,
  subtitle,
  actions,
  backButton,
  children,
  containerId = 'default-header',
}) => {
  const theme = useTheme();
  const { registerSentinel, registerHeader, isStuck } = useStickyHeaderDetection();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerId = `sticky-header-${containerId}`;

  // Register sentinel and header elements
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const header = headerRef.current;

    if (sentinel) {
      registerSentinel(headerId, sentinel);
    }
    if (header) {
      registerHeader(headerId, header);
    }

    return () => {
      registerSentinel(headerId, null);
      registerHeader(headerId, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const headerIsStuck = isStuck(headerId);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Sentinel element for sticky detection */}
      <div
        ref={sentinelRef}
        data-header-id={headerId}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Sticky Header */}
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              py: 3,
              borderBottom: headerIsStuck ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
              boxShadow: headerIsStuck ? '0 2px 4px 0 rgba(0, 0, 0, 0.08)' : 'none',
              transition: headerIsStuck
                ? 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                : 'none', // No transition when unsticking
              clipPath: headerIsStuck
                ? 'inset(-10px 0 -10px 0)' // Only allow shadow on top and bottom
                : 'inset(0 0 0 0)', // No clipping when not stuck
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {backButton && (
                    <ArrowBackIosIcon
                      onClick={backButton.onClick}
                      sx={{
                        cursor: 'pointer',
                        color: 'text.primary',
                        fontSize: '1.25rem',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  )}
                  <Typography variant="h4" sx={{ fontWeight: 500 }}>
                    {title}
                  </Typography>
                </Box>
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>

              {/* Optional actions (buttons, status, etc.) */}
              {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      {children}
    </Box>
  );
};
